/**
 * Upload avec progression réelle (XMLHttpRequest, car fetch API ne supporte pas
 * les events upload progress).
 *
 * @param {string} url - URL de destination
 * @param {FormData|Blob|BufferSource} body - Corps de la requête (souvent FormData)
 * @param {object} [options]
 * @param {Record<string,string>} [options.headers] - Headers HTTP à injecter
 * @param {'POST'|'PUT'|'PATCH'} [options.method='POST']
 * @param {function(number):void} [options.onProgress] - Callback 0..100
 * @param {number} [options.timeout=120000] - Timeout ms (2min par défaut pour photos)
 * @returns {Promise<{ok:boolean, status:number, json:()=>Promise<any>, text:()=>Promise<string>}>}
 */
export function uploadWithProgress(url, body, options = {}) {
  // ── Smart signature detection ───────────────────────────────────────────
  // Supporte 2 formes d'appel :
  //   (A) uploadWithProgress(url, formData, { method, headers, onProgress })
  //   (B) uploadWithProgress(url, { method, headers, body: formData, onProgress })
  let realBody = body;
  let realOptions = options;
  if (
    body &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer) &&
    !ArrayBuffer.isView(body) &&
    !(typeof body.pipeTo === 'function')
  ) {
    // Le 2ème arg est un objet d'options, pas le body réel
    const obj = body;
    realBody = obj.body;
    realOptions = { ...options };
    for (const k of Object.keys(obj)) {
      if (k !== 'body' && !(k in realOptions)) {
        realOptions[k] = obj[k];
      }
    }
  }

  const {
    headers = {},
    method = 'POST',
    onProgress,
    timeout = 120000,
  } = realOptions;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.timeout = timeout;

    // Injecter les headers (sauf Content-Type qui est auto-géré par le navigateur
    // pour les FormData, sinon laisser le caller gérer)
    let contentTypeExplicit = false;
    for (const [k, v] of Object.entries(headers || {})) {
      const lk = String(k).toLowerCase();
      if (lk === 'content-type') {
        // Si le caller a explicitement retiré Content-Type (undefined/null),
        // ne pas l'envoyer pour laisser le navigateur gérer multipart boundary
        if (v == null || v === '') continue;
        contentTypeExplicit = true;
      }
      xhr.setRequestHeader(k, String(v));
    }
    // Pour les FormData on ne JAMAIS forcer Content-Type — le browser ajoute
    // multipart/form-data; boundary=... automatiquement.

    let lastPct = -1;
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (ev) => {
        if (!ev.lengthComputable) return;
        const pct = Math.max(0, Math.min(100, (ev.loaded / ev.total) * 100));
        const rounded = Math.round(pct * 10) / 10;
        if (rounded !== lastPct) {
          lastPct = rounded;
          try { onProgress(rounded); } catch { /* */ }
        }
      };
    }

    xhr.onload = () => {
      const status = xhr.status || 0;
      const ok = status >= 200 && status < 300;
      resolve({
        ok,
        status,
        responseText: xhr.responseText ?? '',
        headers: {
          get: (name) => xhr.getResponseHeader(name),
        },
        text: async () => xhr.responseText ?? '',
        json: async () => {
          try {
            const t = xhr.responseText ?? '';
            return t ? JSON.parse(t) : {};
          } catch (e) {
            throw new Error('Réponse non-JSON: ' + String(xhr.responseText || '').slice(0, 80));
          }
        },
      });
    };

    xhr.onerror = () => reject(new Error('Network error (upload)'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.ontimeout = () => reject(new Error('Upload timeout'));

    try {
      xhr.send(realBody);
    } catch (sendErr) {
      reject(sendErr instanceof Error ? sendErr : new Error(String(sendErr)));
    }
  });
}

export default uploadWithProgress;
